/**
 * Demo visual language: type system + palette derived from the product's
 * brand accent (workflow.demoStyle). Familjen Grotesk carries headlines,
 * Instrument Serif italic is the editorial accent, Geist Mono does labels
 * and browser chrome.
 */
import { loadFont as loadGrotesk } from '@remotion/google-fonts/FamiljenGrotesk'
import { loadFont as loadSerif } from '@remotion/google-fonts/InstrumentSerif'
import { loadFont as loadMono } from '@remotion/google-fonts/GeistMono'
import type { DemoStyle } from './types'

const grotesk = loadGrotesk('normal', { weights: ['500', '600', '700'] })
const serif = loadSerif('italic', { weights: ['400'] })
const mono = loadMono('normal', { weights: ['400', '500'] })

export const demoFonts = {
  headline: grotesk.fontFamily,
  serif: serif.fontFamily,
  mono: mono.fontFamily,
}

export type DemoPalette = {
  bg: string
  bgRaised: string
  text: string
  textDim: string
  accent: string
  accent2: string
  chromeBg: string
  chromeBorder: string
  urlText: string
  dark: boolean
}

export function demoPalette(style: DemoStyle | undefined): DemoPalette {
  const accent = style?.accent ?? '#5e6ad2'
  const accent2 = style?.accentSecondary ?? accent
  if (style?.theme === 'light') {
    return {
      bg: '#f4f3f0',
      bgRaised: '#ffffff',
      text: '#171513',
      textDim: 'rgba(23,21,19,0.55)',
      accent,
      accent2,
      chromeBg: '#ffffff',
      chromeBorder: 'rgba(0,0,0,0.10)',
      urlText: 'rgba(23,21,19,0.65)',
      dark: false,
    }
  }
  return {
    bg: '#0a0b0f',
    bgRaised: '#13151c',
    text: '#f2f3f7',
    textDim: 'rgba(242,243,247,0.55)',
    accent,
    accent2,
    chromeBg: '#181a21',
    chromeBorder: 'rgba(255,255,255,0.10)',
    urlText: 'rgba(242,243,247,0.60)',
    dark: true,
  }
}

/** Hex → rgba string with alpha. */
export function alpha(hex: string, a: number): string {
  const m = hex.replace('#', '')
  const n = m.length === 3 ? m.split('').map((c) => c + c).join('') : m
  const r = parseInt(n.slice(0, 2), 16)
  const g = parseInt(n.slice(2, 4), 16)
  const b = parseInt(n.slice(4, 6), 16)
  if ([r, g, b].some((x) => Number.isNaN(x))) return `rgba(94,106,210,${a})`
  return `rgba(${r},${g},${b},${a})`
}
